import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import moment from 'jalali-moment';
import { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import endpoints from '../../config/endpoints';
import { useTheme } from '../../contexts/ThemeContext';
import { Header } from '../../layouts/Header';
import { Sidebar } from '../../layouts/Sidebar';
import restApi from '../../services/restApi';
import tools from '../../utils/tools';
import './dashboard.page.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

/* ── Types ──────────────────────────────────────────────── */
interface SummaryCard {
  totalRevenue: number;
  totalRevenueChange: number;
  totalOrders: number;
  totalOrdersChange: number;
  newUsers: number;
  newUsersChange: number;
  activeProducts: number;
}

interface TimePoint { date: string; revenue: number; orders: number; }
interface StatusPoint { slug: string; title: string; count: number; }
interface UserPoint   { date: string; count: number; }
interface TopProduct  { id: number; title: string; soldCount: number; revenue: number; }
interface RecentOrder {
  id: number; code: string;
  user: { name: string };
  price: number;
  orderStatusSlug: string;
  createdAt: string;
}

interface DashboardData {
  summary: SummaryCard;
  revenueOverTime: TimePoint[];
  ordersByStatus: StatusPoint[];
  newUsersOverTime: UserPoint[];
  topProducts: TopProduct[];
  recentOrders: RecentOrder[];
}

/* ── Date preset helpers ─────────────────────────────────── */
type Preset = '7d' | '30d' | '90d' | '1y' | 'custom';

const presets: { key: Preset; label: string; days: number }[] = [
  { key: '7d',  label: 'هفت روز اخیر',    days: 7   },
  { key: '30d', label: 'سی روز اخیر',     days: 30  },
  { key: '90d', label: 'سه ماه اخیر',     days: 90  },
  { key: '1y',  label: 'یک سال اخیر',     days: 365 },
  { key: 'custom', label: 'بازه دلخواه',  days: 0   },
];

const toIso = (d: Date) => d.toISOString().split('T')[0];
const presetRange = (days: number) => {
  const end   = new Date();
  const start = new Date(Date.now() - days * 86_400_000);
  return { start: toIso(start), end: toIso(end) };
};

/* ── Chart colour palette ────────────────────────────────── */
const palette = {
  primary:   '#e84d0f',
  blue:      '#3b82f6',
  green:     '#10b981',
  amber:     '#f59e0b',
  purple:    '#8b5cf6',
  pink:      '#ec4899',
  teal:      '#14b8a6',
};

const statusColors: Record<string, string> = {
  setPrice:      palette.amber,
  callPrice:     palette.blue,
  clientApproved:palette.green,
  accounting:    palette.purple,
  packing:       palette.teal,
  sent:          palette.pink,
  default:       palette.primary,
};

const STATUS_LABEL: Record<string, string> = {
  setPrice:      'در انتظار قیمت',
  callPrice:     'اعلام قیمت',
  clientApproved:'تایید خرید',
  accounting:    'حسابداری',
  packing:       'بسته‌بندی',
  sent:          'ارسال شده',
};

/* ═══════════════════════════════════════════════════════════
   Component
═══════════════════════════════════════════════════════════ */
const Dashboard = () => {
  const { theme } = useTheme();
  const navigate  = useNavigate();

  const [preset, setPreset]   = useState<Preset>('30d');
  const [startDate, setStart] = useState(toIso(new Date(Date.now() - 30 * 86_400_000)));
  const [endDate,   setEnd]   = useState(toIso(new Date()));
  const [data,  setData]      = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  /* ── Chart theme tokens ──────────────────────────────── */
  const dark   = theme === 'dark';
  const grid   = dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';
  const label  = dark ? '#8b949e'                : '#6b7280';
  const ttBg   = dark ? '#1c2333'                : '#ffffff';
  const ttBdr  = dark ? '#21262d'                : '#e5e7eb';
  const ttTxt  = dark ? '#e2e8f0'                : '#0f1923';

  const baseChartOpts = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: label, font: { family: 'Yekan Bakh', size: 12 } } },
      tooltip: {
        backgroundColor: ttBg,
        borderColor: ttBdr,
        borderWidth: 1,
        titleColor: ttTxt,
        bodyColor: ttTxt,
        padding: 10,
        titleFont: { family: 'Yekan Bakh' },
        bodyFont:  { family: 'Yekan Bakh' },
      },
    },
    scales: {
      x: { grid: { color: grid }, ticks: { color: label, font: { family: 'Yekan Bakh' } } },
      y: { grid: { color: grid }, ticks: { color: label, font: { family: 'Yekan Bakh' } } },
    },
  }), [dark, grid, label, ttBg, ttBdr, ttTxt]);

  /* ── Fetch ───────────────────────────────────────────── */
  const fetchData = async (s: string, e: string) => {
    setLoading(true);
    try {
      const res = await restApi(`${endpoints.dashboard}?startDate=${s}&endDate=${e}`, true).get();
      if (res.data) setData(res.data);
    } catch {/* handled by global loading */}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(startDate, endDate); }, [startDate, endDate]);

  /* ── Preset change ───────────────────────────────────── */
  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      const days = presets.find(x => x.key === p)!.days;
      const { start, end } = presetRange(days);
      setStart(start); setEnd(end);
    }
  };

  /* ── Chart datasets ──────────────────────────────────── */
  const revenueLabels = data?.revenueOverTime.map(d =>
    moment(d.date).format('jMM/jDD')) ?? [];

  const revenueData = {
    labels: revenueLabels,
    datasets: [
      {
        label: 'درآمد (تومان)',
        data: data?.revenueOverTime.map(d => d.revenue) ?? [],
        borderColor: palette.primary,
        backgroundColor: `${palette.primary}22`,
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
      },
      {
        label: 'تعداد سفارش',
        data: data?.revenueOverTime.map(d => d.orders) ?? [],
        borderColor: palette.blue,
        backgroundColor: 'transparent',
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
        yAxisID: 'y2',
      },
    ],
  };

  const revenueOpts: any = {
    ...baseChartOpts,
    scales: {
      ...baseChartOpts.scales,
      y:  { ...baseChartOpts.scales.y, position: 'left',  title: { display: true, text: 'تومان', color: label, font: { family: 'Yekan Bakh' } } },
      y2: { ...baseChartOpts.scales.y, position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'سفارش', color: label, font: { family: 'Yekan Bakh' } } },
    },
  };

  const doughnutData = {
    labels: data?.ordersByStatus.map(s => STATUS_LABEL[s.slug] ?? s.title) ?? [],
    datasets: [{
      data: data?.ordersByStatus.map(s => s.count) ?? [],
      backgroundColor: data?.ordersByStatus.map(s => statusColors[s.slug] ?? palette.primary) ?? [],
      borderWidth: 2,
      borderColor: dark ? '#161b27' : '#ffffff',
      hoverBorderWidth: 0,
    }],
  };

  const doughnutOpts: any = {
    ...baseChartOpts,
    cutout: '68%',
    scales: undefined,
    plugins: {
      ...baseChartOpts.plugins,
      legend: {
        ...baseChartOpts.plugins.legend,
        position: 'bottom' as const,
        labels: { ...baseChartOpts.plugins.legend.labels, padding: 16, boxWidth: 12, boxHeight: 12 },
      },
    },
  };

  const usersLabels = data?.newUsersOverTime.map(d => moment(d.date).format('jMM/jDD')) ?? [];
  const usersData = {
    labels: usersLabels,
    datasets: [{
      label: 'کاربران جدید',
      data: data?.newUsersOverTime.map(d => d.count) ?? [],
      backgroundColor: `${palette.green}cc`,
      borderRadius: 6,
      borderSkipped: false,
    }],
  };

  const topProductsData = {
    labels: data?.topProducts.map(p => p.title.length > 20 ? p.title.slice(0, 20) + '…' : p.title) ?? [],
    datasets: [{
      label: 'درآمد (تومان)',
      data: data?.topProducts.map(p => p.revenue) ?? [],
      backgroundColor: data?.topProducts.map((_, i) =>
        Object.values(palette)[i % Object.values(palette).length] + 'cc') ?? [],
      borderRadius: 6,
      borderSkipped: false,
    }],
  };
  const topProductsOpts: any = {
    ...baseChartOpts,
    indexAxis: 'y' as const,
    scales: {
      x: baseChartOpts.scales.x,
      y: { ...baseChartOpts.scales.y, ticks: { ...baseChartOpts.scales.y.ticks, font: { family: 'Yekan Bakh', size: 11 } } },
    },
  };

  /* ── Stat cards data ─────────────────────────────────── */
  const cards = [
    {
      title: 'درآمد کل',
      value: data ? tools.formatPrice(data.summary.totalRevenue) : '—',
      change: data?.summary.totalRevenueChange ?? 0,
      icon: '💰',
      color: palette.primary,
    },
    {
      title: 'تعداد سفارش',
      value: data?.summary.totalOrders ?? '—',
      change: data?.summary.totalOrdersChange ?? 0,
      icon: '📦',
      color: palette.blue,
    },
    {
      title: 'کاربران جدید',
      value: data?.summary.newUsers ?? '—',
      change: data?.summary.newUsersChange ?? 0,
      icon: '👤',
      color: palette.green,
    },
    {
      title: 'محصولات فعال',
      value: data?.summary.activeProducts ?? '—',
      change: null,
      icon: '🛍️',
      color: palette.purple,
    },
  ];

  /* ── Render ──────────────────────────────────────────── */
  return (
    <div className="dashboardBody">
      <Sidebar />
      <div className="dashBoardMain">
        <Header title="داشبورد" />
        <div className="dashBoardInner">

          {/* ── Date range controls ───────────────────── */}
          <div className="db-date-bar">
            <div className="db-presets">
              {presets.map(p => (
                <button
                  key={p.key}
                  className={`db-preset-btn ${preset === p.key ? 'active' : ''}`}
                  onClick={() => applyPreset(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === 'custom' && (
              <div className="db-custom-range">
                <input
                  type="date" className="db-date-input"
                  value={startDate}
                  onChange={e => setStart(e.target.value)}
                />
                <span className="db-date-sep">تا</span>
                <input
                  type="date" className="db-date-input"
                  value={endDate}
                  onChange={e => setEnd(e.target.value)}
                />
              </div>
            )}
            <span className="db-range-label">
              {moment(startDate).format('jYYYY/jMM/jDD')} — {moment(endDate).format('jYYYY/jMM/jDD')}
            </span>
          </div>

          {/* ── Summary cards ─────────────────────────── */}
          <div className="db-cards">
            {cards.map((c, i) => (
              <div key={i} className="db-card" style={{ '--card-accent': c.color } as any}>
                <div className="db-card-icon">{c.icon}</div>
                <div className="db-card-body">
                  <p className="db-card-title">{c.title}</p>
                  <p className="db-card-value">
                    {loading ? <span className="db-skeleton" /> : c.value}
                  </p>
                  {c.change !== null && !loading && (
                    <span className={`db-card-change ${c.change >= 0 ? 'up' : 'down'}`}>
                      {c.change >= 0 ? '▲' : '▼'} {Math.abs(c.change).toFixed(1)}٪ نسبت به قبل
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* ── Row 1: Revenue line + Doughnut ───────── */}
          <div className="db-row">
            <div className="dashCard db-chart-main">
              <div className="db-chart-header">
                <span className="db-chart-title">درآمد و تعداد سفارش</span>
              </div>
              <div className="db-chart-body" style={{ height: 280 }}>
                {loading
                  ? <div className="db-chart-skeleton" />
                  : <Line data={revenueData} options={revenueOpts} />
                }
              </div>
            </div>

            <div className="dashCard db-chart-side">
              <div className="db-chart-header">
                <span className="db-chart-title">وضعیت سفارش‌ها</span>
              </div>
              <div className="db-chart-body" style={{ height: 280 }}>
                {loading
                  ? <div className="db-chart-skeleton" />
                  : <Doughnut data={doughnutData} options={doughnutOpts} />
                }
              </div>
            </div>
          </div>

          {/* ── Row 2: Users bar + Top products bar ──── */}
          <div className="db-row">
            <div className="dashCard db-chart-half">
              <div className="db-chart-header">
                <span className="db-chart-title">ثبت‌نام کاربران جدید</span>
              </div>
              <div className="db-chart-body" style={{ height: 240 }}>
                {loading
                  ? <div className="db-chart-skeleton" />
                  : <Bar data={usersData} options={baseChartOpts as any} />
                }
              </div>
            </div>

            <div className="dashCard db-chart-half">
              <div className="db-chart-header">
                <span className="db-chart-title">پرفروش‌ترین محصولات</span>
              </div>
              <div className="db-chart-body" style={{ height: 240 }}>
                {loading
                  ? <div className="db-chart-skeleton" />
                  : <Bar data={topProductsData} options={topProductsOpts} />
                }
              </div>
            </div>
          </div>

          {/* ── Recent orders mini-table ──────────────── */}
          <div className="dashCard">
            <div className="db-chart-header">
              <span className="db-chart-title">آخرین سفارش‌ها</span>
              <button className="db-view-all" onClick={() => navigate('/order/')}>
                مشاهده همه ←
              </button>
            </div>
            <div className="db-recent-table-wrap">
              <table className="db-recent-table">
                <thead>
                  <tr>
                    <th>شماره سفارش</th>
                    <th>کاربر</th>
                    <th>مبلغ</th>
                    <th>وضعیت</th>
                    <th>تاریخ</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 5 }).map((_, j) => (
                            <td key={j}><span className="db-skeleton" style={{ width: '80%' }} /></td>
                          ))}
                        </tr>
                      ))
                    : (data?.recentOrders ?? []).map(o => (
                        <tr
                          key={o.id}
                          className="db-recent-row"
                          onClick={() => navigate('/order/edit/' + o.id)}
                        >
                          <td><code className="db-code">{o.code}</code></td>
                          <td>{o.user?.name || '—'}</td>
                          <td>{tools.formatPrice(o.price)}</td>
                          <td>
                            <span
                              className="db-status-badge"
                              style={{ background: `${statusColors[o.orderStatusSlug] ?? palette.primary}22`, color: statusColors[o.orderStatusSlug] ?? palette.primary }}
                            >
                              {STATUS_LABEL[o.orderStatusSlug] ?? o.orderStatusSlug}
                            </span>
                          </td>
                          <td>{moment(o.createdAt).format('jYYYY/jMM/jDD')}</td>
                        </tr>
                      ))
                  }
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
